import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { marked } from 'marked';
import mermaid from 'mermaid';
import { ChatService } from './chat.service';
import { ChatOpenService } from './chat-open.service';
import { ChatActionFormComponent } from './chat-action-form.component';
import { ChatConversation, ChatMessage } from '../../models/chat.model';
import { ApiService } from '../../core/services/api.service';

// Use bypassSecurityTrustHtml so data-* attributes survive — the content comes
// from our own Gemini agent, not from arbitrary user input.
// renderMarkdown is memoized so the same SafeHtml object reference is returned for
// the same text, preventing Angular's [innerHTML] binding from recreating the DOM
// node (and wiping out already-rendered SVGs) on every change-detection cycle.
marked.use({
  renderer: {
    code({ text, lang }: { text: string; lang?: string }) {
      if (lang === 'mermaid') {
        const encoded = btoa(encodeURIComponent(text));
        return `<div class="mermaid-block" data-graph="${encoded}"></div>`;
      }
      return false;
    },
  },
});

const SUGGESTED_PROMPTS = [
  { label: 'My open action items',    icon: 'task_alt',   text: 'Show me my open action items'                   },
  { label: 'What can you do?',        icon: 'auto_awesome', text: 'What all can you do?'                        },
  { label: 'Projects ending soon',    icon: 'event',      text: 'Which projects are ending in the next 30 days?' },
  { label: 'Create an action item',   icon: 'add_task',   text: 'Create an action item'                         },
];

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, ChatActionFormComponent],
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.scss',
})
export class ChatWidgetComponent implements OnInit, AfterViewChecked {
  protected chat = inject(ChatService);
  private sanitizer = inject(DomSanitizer);
  private chatOpenSvc = inject(ChatOpenService);
  private destroyRef = inject(DestroyRef);
  private api = inject(ApiService);

  @ViewChild('messageList') private messageList!: ElementRef<HTMLDivElement>;

  open = signal(false);
  maximized = signal(false);
  sidebarVisible = signal(true);
  inputText = signal('');

  readonly suggestedPrompts = SUGGESTED_PROMPTS;

  // Inline title editing
  editingSessionId: string | null = null;
  editingTitle = '';

  ngOnInit() {
    // Open maximized from sidebar nav item
    this.chatOpenSvc.open$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.open()) {
        this.open.set(true);
        this.maximized.set(true);
        this.logOpen();
        this.chat.newConversation();
        this.chat.loadConversations();
        setTimeout(() => this.scrollToBottom(), 50);
      }
    });
    this.chat.loadConversations();
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    mermaid.initialize({
      startOnLoad: false,
      theme: isDark ? 'dark' : 'neutral',
      securityLevel: 'loose',
      fontFamily: 'Google Sans, Roboto, sans-serif',
      logLevel: 5, // suppress mermaid's internal error toasts
    });
  }

  ngAfterViewChecked() {
    const blocks = this.messageList?.nativeElement
      ?.querySelectorAll<HTMLElement>('.mermaid-block:not([data-rendered])');
    if (!blocks?.length) return;
    blocks.forEach(block => {
      block.setAttribute('data-rendered', 'true');
      const encoded = block.getAttribute('data-graph') ?? '';
      let graph: string;
      try {
        graph = decodeURIComponent(atob(encoded));
      } catch {
        block.innerHTML = '<p class="mermaid-error">Could not decode diagram.</p>';
        return;
      }
      if (!graph) {
        block.innerHTML = '<p class="mermaid-error">No diagram content.</p>';
        return;
      }
      const renderId = `mgr${Math.random().toString(36).slice(2, 11)}`;
      const doRender = async () => {
        try {
          const { svg } = await mermaid.render(renderId, graph);
          // Wrap SVG with a toolbar containing the download button
          block.innerHTML = `
            <div class="mermaid-toolbar">
              <button class="mermaid-download-btn" title="Download diagram source">
                <span class="material-icons" style="font-size:16px;vertical-align:middle">download</span>
                Download .mmd
              </button>
            </div>
            <div class="mermaid-svg-wrap">${svg}</div>
          `;
          block.querySelector('.mermaid-download-btn')?.addEventListener('click', () => {
            const blob = new Blob([graph], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'diagram.mmd';
            a.click();
            URL.revokeObjectURL(url);
          });
        } catch (err) {
          console.error('[mermaid] render failed\n---\n' + graph + '\n---', err);
          block.innerHTML = '<p class="mermaid-error">Diagram syntax error — check the generated mermaid code.</p>';
        }
      };
      doRender();
    });
  }

  toggleSidebar() { this.sidebarVisible.update(v => !v); }

  toggle() {
    const opening = !this.open();
    this.open.set(opening);
    if (opening) {
      this.logOpen();
      this.chat.newConversation();
      this.chat.loadConversations();
      setTimeout(() => this.scrollToBottom(), 50);
    } else {
      this.maximized.set(false);
    }
  }

  private logOpen(): void {
    this.api.post('/api/v1/chat/open', {}).subscribe({ error: () => {} });
  }

  close() {
    this.open.set(false);
    this.maximized.set(false);
  }

  toggleMaximize() {
    this.maximized.update(v => !v);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  async send() {
    const text = this.inputText().trim();
    if (!text) return;
    if (this.chat.streaming()) return;
    this.inputText.set('');
    await this.chat.sendMessage(text);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  newConversation() {
    this.chat.newConversation();
    this.chat.loadConversations();
    this.cancelEdit();
  }

  async selectConversation(conv: ChatConversation) {
    if (this.editingSessionId === conv.session_id) return;
    await this.chat.loadConversation(conv.session_id);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  async deleteConversation(event: Event, sessionId: string) {
    event.stopPropagation();
    await this.chat.deleteConversation(sessionId);
    if (this.editingSessionId === sessionId) this.cancelEdit();
  }

  startEdit(event: Event, conv: ChatConversation) {
    event.stopPropagation();
    this.editingSessionId = conv.session_id;
    this.editingTitle = conv.title;
    setTimeout(() => {
      const el = document.getElementById(`title-input-${conv.session_id}`);
      el?.focus();
      (el as HTMLInputElement)?.select();
    }, 30);
  }

  saveEdit(sessionId: string) {
    if (this.editingTitle.trim()) {
      this.chat.renameConversation(sessionId, this.editingTitle);
    }
    this.cancelEdit();
  }

  cancelEdit() {
    this.editingSessionId = null;
    this.editingTitle = '';
  }

  onTitleKeydown(event: KeyboardEvent, sessionId: string) {
    if (event.key === 'Enter') { event.preventDefault(); this.saveEdit(sessionId); }
    if (event.key === 'Escape') { this.cancelEdit(); }
  }

  private _mdCache = new Map<string, SafeHtml>();

  renderMarkdown(text: string): SafeHtml {
    if (!text) return '';
    const cached = this._mdCache.get(text);
    if (cached) return cached;
    const raw = marked.parse(text, { async: false }) as string;
    // bypassSecurityTrustHtml so data-graph survives — content is from our own LLM agent.
    // Memoized so the same SafeHtml reference is returned for unchanged text, preventing
    // Angular from recreating the DOM node and wiping already-rendered mermaid SVGs.
    const safe = this.sanitizer.bypassSecurityTrustHtml(raw);
    this._mdCache.set(text, safe);
    return safe;
  }

  async onFormSubmit(data: Record<string, string>, msg: ChatMessage) {
    if (!msg.formRequest) return;
    this.chat.markFormSubmitted(msg.id);
    await this.chat.submitForm(msg.formRequest.tool, msg.formRequest.title, data);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  async sendSuggestion(text: string) {
    if (this.chat.streaming()) return;
    await this.chat.sendMessage(text);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  async sendPrompt(p: { text: string }) {
    if (this.chat.streaming()) return;
    this.inputText.set('');
    await this.chat.sendMessage(p.text);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  hasTable(content: string): boolean {
    return /^\|.+\|/m.test(content);
  }

  exportCsv(content: string, msgId: string) {
    const tables = this._parseMarkdownTables(content);
    if (!tables.length) return;
    const csv = tables.map(t => {
      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      return [
        t.headers.map(escape).join(','),
        ...t.rows.map(r => r.map(escape).join(',')),
      ].join('\n');
    }).join('\n\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `ask-wave-export-${msgId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private _parseMarkdownTables(md: string): { headers: string[]; rows: string[][] }[] {
    const results: { headers: string[]; rows: string[][] }[] = [];
    const lines = md.split('\n');
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (line.includes('|') && lines[i + 1]?.match(/^\|[\s\-:|]+\|/)) {
        const headers = line.split('|').map(h => h.trim()).filter(Boolean);
        i += 2;
        const rows: string[][] = [];
        while (i < lines.length && lines[i].includes('|')) {
          rows.push(lines[i].split('|').map(c => c.trim()).filter(Boolean));
          i++;
        }
        results.push({ headers, rows });
      } else {
        i++;
      }
    }
    return results;
  }

  private scrollToBottom() {
    const el = this.messageList?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
